import type { v2, ControlTypeFunction, ControlIdFunction } from '@sap/ux-specification-types';
import { ControlType, FACETTITLEPREFIX } from '@sap/ux-specification-types';
import { syncRule, addPatternForBindingChangeOfEnumOP } from '../../../common';

const PREFIXHEADEREDITABLE = 'header::headerEditable::';

/**
 * Create stable ID for Object Page DataPoint Header Section.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumb IDs used for identification
 * @param {string[]} _breadcrumbs Array of breadcrumb strings used for navigation or identification
 * @param {ControlTypeFunction} _controlType Function to determine the control type
 * @param title Title of the header section
 * @returns {string} stable ID of Object Page Header Action button
 */
const getDataPointHeaderSectionId = (
    baseId: string,
    _idBreadcrumbs: string[],
    _breadcrumbs: string[],
    _controlType: ControlTypeFunction,
    title: string
): string => {
    return `${baseId}${PREFIXHEADEREDITABLE}${title}`;
};

/**
 * Create stable ID for Object Page Address Header Section.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumb IDs used for identification
 * @param {string[]} _breadcrumbs Array of breadcrumb strings used for navigation or identification
 * @param {ControlTypeFunction} _controlType Function to determine the control type
 * @param title Title of the header section
 * @returns {string} stable ID of Object Page Header Action button
 */
const getAddressHeaderSectionId = (
    baseId: string,
    _idBreadcrumbs: string[],
    _breadcrumbs: string[],
    _controlType: ControlTypeFunction,
    title: string
): string => {
    return `${baseId}${PREFIXHEADEREDITABLE}${title}::ContactAddress`;
};

/**
 * Create stable ID for Object Page Chart Header Section.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumb IDs used for identification
 * @param {string[]} _breadcrumbs Array of breadcrumb strings used for navigation or identification
 * @param {ControlTypeFunction} _controlType Function to determine the control type
 * @param title Title of the header section
 * @returns {string} stable ID of Object Page Header Action button
 */
const getChartHeaderSectionId = (
    baseId: string,
    _idBreadcrumbs: string[],
    _breadcrumbs: string[],
    _controlType: ControlTypeFunction,
    title: string
): string => {
    return `${baseId}${PREFIXHEADEREDITABLE}${title}::SmartMicroChartVBox`;
};

/**
 * Create stable ID for Object Page Form Header Section.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs Array of breadcrumb IDs used for identification
 * @param {string[]} breadcrumbs Array of breadcrumb strings used for navigation or identification
 * @param {ControlTypeFunction} _controlType Function to determine the control type
 * @param title Title of the header section
 * @returns {string} stable ID of Object Page Header Action button
 */
const getFormHeaderSectionId = (
    baseId: string,
    _idBreadcrumbs: string[],
    breadcrumbs: string[],
    _controlType: ControlTypeFunction,
    title: string
): string => {
    const sectionName = breadcrumbs[breadcrumbs.length - 1].replace(/@com.sap/g, 'com.sap');
    const facetId = title?.includes(FACETTITLEPREFIX) ? title.split(FACETTITLEPREFIX)[1] : sectionName;
    return `${baseId}${PREFIXHEADEREDITABLE}${facetId}::Form`;
};

const flexRuleForHeaderSection = {
    controlType: (): ControlType => ControlType.ObjectPageHeaderSection
};

export class CommonHeaderFacetSettings implements v2.CommonHeaderFacetSettings {
    @syncRule({
        flex: {
            ...flexRuleForHeaderSection,
            // Placeholder for stable ID, will be replaced by the specific section
            controlId: (() => crypto.randomUUID() as string) satisfies ControlIdFunction
        },
        generate: addPatternForBindingChangeOfEnumOP
    })
    visible?: boolean;
}

export class ObjectPageHeaderSectionForm extends CommonHeaderFacetSettings implements v2.ObjectPageHeaderSectionForm {
    @syncRule({
        flex: {
            ...flexRuleForHeaderSection,
            controlId: getFormHeaderSectionId
        }
    })
    declare visible?: boolean;
}

export class ObjectPageHeaderSectionChart extends CommonHeaderFacetSettings implements v2.ObjectPageHeaderSectionChart {
    @syncRule({
        flex: {
            ...flexRuleForHeaderSection,
            controlId: getChartHeaderSectionId
        }
    })
    declare visible?: boolean;
}

export class ObjectPageHeaderSectionDataPoint
    extends CommonHeaderFacetSettings
    implements v2.ObjectPageHeaderSectionDataPoint
{
    @syncRule({
        flex: {
            ...flexRuleForHeaderSection,
            controlId: getDataPointHeaderSectionId
        }
    })
    declare visible?: boolean;
}

export class ObjectPageHeaderSectionAddress
    extends CommonHeaderFacetSettings
    implements v2.ObjectPageHeaderSectionAddress
{
    @syncRule({
        flex: {
            ...flexRuleForHeaderSection,
            controlId: getAddressHeaderSectionId
        }
    })
    declare visible?: boolean;
}

export class HeaderSections implements v2.HeaderSections {
    [key: string]:
        | ObjectPageHeaderSectionAddress
        | ObjectPageHeaderSectionChart
        | ObjectPageHeaderSectionDataPoint
        | ObjectPageHeaderSectionForm;
}
